import { Session } from '@colplat/core-client';
import {
  askConfirm, escapeHtml, formString, mountCollabField, mountCollabToggle, renderRecordTable,
  type MountedField, type RecordTableRow,
} from '@colplat/ui-web';
import { apiFetch, failureMessage } from '../api.js';
import { readCached } from '../offlineReads.js';
import {
  bootstrapWebledgerClient, clearNoticeAt, clearPageNotice, pageFailure, showNoticeAt,
  webledgerShellHtml, wireBackButton,
} from '../webledgerClient.js';
import { SHOW_PURPOSES } from './webledgerFeatures.js';
import { loadTags, tagChipHtml, type TagDto } from './webledgerTags.js';

/** Currencies + purposes — the smaller, occasionally-touched reference data, tucked away from the two everyday pages (accounts, transactions). */

interface BootstrapModalLike { show(): void; hide(): void }
function bsModal(el: Element): BootstrapModalLike | undefined {
  return (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(e: Element): BootstrapModalLike } } })
    .bootstrap?.Modal.getOrCreateInstance(el);
}

interface CurrencyDto {
  id: string; code: string; name: string; symbol: string; fraction: number; useForTotals: boolean;
  /** Set by dragging the rows here; see `currencyRank`. */
  sortOrder: number;
}
interface PurposeDto { id: string; name: string; sortOrder: number }

export async function mountWebledgerSettings(root: HTMLElement): Promise<void> {
  root.innerHTML = shellHtml();
  wireBackButton(root);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  // Destructured rather than reached through `client` in the nested render
  // functions below: the narrowing from `if (!client) return` does not follow
  // into every closure, and `notify` is a plain arrow function with no `this`
  // to lose.
  const { token, session, notify } = client;
  /**
   * Announced *and* on screen (`pageFailure`). Everything on this page used
   * to refuse into `notify` alone — the `.cf-sr` region — so a tag that
   * would not delete, an order that would not save and a purpose the server
   * declined all looked identical to nothing having happened.
   */
  const fail = pageFailure(root, notify);

  const { docCache } = client;
  const [currenciesRead, purposesRead] = await Promise.all([
    readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token),
    readCached<{ purposes: PurposeDto[] }>(docCache, 'purposes', '/apps/webledger/purposes', token),
  ]);
  if (!currenciesRead.ok && currenciesRead.status === 403) {
    root.querySelector('#wlBody')!.innerHTML = '<p class="text-secondary">You do not have access to this app.</p>';
    return;
  }
  if (!currenciesRead.ok && !purposesRead.ok) {
    root.querySelector('#wlBody')!.innerHTML =
      '<p class="text-secondary">This page has not been loaded on this device yet, and there is no connection. ' +
      'Open it once while online and it will keep working offline afterwards.</p>';
    return;
  }
  // Either half is worth showing on its own — see the note in
  // webledgerAccounts.ts. The transactions page caches `currencies` but not
  // `purposes`, so arriving here offline should still show the currencies.
  let currencies = currenciesRead.ok ? currenciesRead.data.currencies : [];
  let purposes = purposesRead.ok ? purposesRead.data.purposes : [];

  client.transport.onMessage((msg) => {
    if (msg.t === 'recordEvent' && (msg.entity === 'currency' || msg.entity === 'purpose')) {
      void refreshAll();
    }
  });

  let tags: TagDto[] = [];
  let tagUsage: Record<string, number> = {};

  async function refreshTags(): Promise<void> {
    const [list, usageRes] = await Promise.all([
      loadTags(token),
      apiFetch('/apps/webledger/tags/usage', { token }).catch(() => undefined),
    ]);
    tags = list;
    tagUsage = usageRes?.ok ? ((await usageRes.json()) as { usage: Record<string, number> }).usage : {};
    renderTags();
  }
  function renderTags(): void {
    renderTagsTable(root, tags, tagUsage);
    wireTagDrag(root, token, refreshTags, fail);
  }
  wireTagActions(root, token, () => tags, () => tagUsage, refreshTags, fail);
  wireNewTagForm(root, token, notify, () => { void refreshTags(); });
  void refreshTags();

  wireNewCurrencyForm(root, token, refreshCurrencies);
  void mountExchangeRates(root, token);
  wireNewPurposeForm(root, token, refreshPurposes);

  renderCurrencies();
  renderPurposes();

  // `!read.stale`: a refresh that fell back to the cache has nothing new — the
  // same data is already rendered.
  async function refreshCurrencies(): Promise<void> {
    const read = await readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token);
    if (!read.ok || read.stale) return;
    currencies = read.data.currencies;
    renderCurrencies();
  }
  async function refreshPurposes(): Promise<void> {
    const read = await readCached<{ purposes: PurposeDto[] }>(docCache, 'purposes', '/apps/webledger/purposes', token);
    if (!read.ok || read.stale) return;
    purposes = read.data.purposes;
    renderPurposes();
  }
  async function refreshAll(): Promise<void> {
    await Promise.all([refreshCurrencies(), refreshPurposes()]);
  }

  function renderCurrencies(): void {
    renderCurrenciesTable(root, session, currencies, (currency) => openCurrencyModal(root, session, currency));
    // The order here is the order every list of currencies takes — the
    // columns of a breakdown, the lines of a chart.
    wireFlatReorder(root.querySelector<HTMLElement>('#currenciesTable')!, 'row',
      '/apps/webledger/currencies/reorder', token, refreshCurrencies, fail);
  }
  function renderPurposes(): void {
    renderPurposesTable(root, purposes);
    // Only the drag handlers here: they live on the rows, which this render
    // has just replaced. The delete button is delegated from the container
    // and wired once, below — wiring it here added a listener per render, so
    // after two renders one click asked three times whether to delete.
    wireFlatReorder(root.querySelector<HTMLElement>('#purposesTable')!, 'purpose',
      '/apps/webledger/purposes/reorder', token, refreshPurposes, fail);
  }
  wirePurposeDeletes(root, token, () => purposes, refreshPurposes, fail);
}

function shellHtml(): string {
  return webledgerShellHtml('settings', `
    <div class="row g-3">
      <div class="col-md-6">
        <div class="cf-card cf-card--bleed p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="cf-h">Currencies</div>
            <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="modal" data-bs-target="#newCurrencyModal">New currency</button>
          </div>
          <div id="currenciesTable"></div>
        </div>
      </div>
      ${SHOW_PURPOSES ? `
      <div class="col-md-6">
        <div class="cf-card cf-card--bleed p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="cf-h">Purposes</div>
            <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="modal" data-bs-target="#newPurposeModal">New purpose</button>
          </div>
          <div id="purposesTable"></div>
        </div>
      </div>` : ''}
      <div class="col-12">
        <div class="cf-card cf-card--bleed p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="cf-h">Tags</div>
            <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="modal" data-bs-target="#newTagModal">New tag</button>
          </div>
          <p class="text-secondary small mb-2">
            Tags label a <em>transaction</em> — this renovation, this trip. Filtering a report
            by tag totals the expense and income legs of the transactions it matches, since a
            whole tagged transaction balances to zero.
          </p>
          <div id="tagsTable"></div>
        </div>
      </div>
      <div class="col-12">
        <div class="cf-card cf-card--bleed p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="cf-h">Exchange rates</div>
            <button class="btn btn-sm btn-outline-secondary" type="button" id="wlFxRefresh">Refresh now</button>
          </div>
          <div id="wlFxStatus" class="small text-secondary" role="status">Loading…</div>
          <div class="small text-secondary mt-2">
            Per-transaction exchange rates live inside each transaction, in its own value/amount pair.
            These rates are only used to convert report totals into a single currency, and are never stored
            on a transaction. Fetched once a day; a currency appears above only if it is marked
            &ldquo;offer for folder totals&rdquo;.
          </div>
        </div>
      </div>
    </div>`) +
    currencyModalHtml() + newCurrencyModalHtml() + newPurposeModalHtml() + newTagModalHtml() +
    `<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}

// ------------------------------------------------------------ currencies

function currencyModalHtml(): string {
  return `
    <div class="modal fade" id="currencyModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold" id="currencyModalTitle">Currency</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body pt-4" id="currencyModalFields"></div>
        </div>
      </div>
    </div>`;
}

function newCurrencyModalHtml(): string {
  return `
    <div class="modal fade" id="newCurrencyModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">New currency</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="newCurrencyForm">
            <div class="modal-body">
              <div class="mb-2"><label class="form-label">Code</label><input class="form-control" name="code" maxlength="3" placeholder="EUR" required></div>
              <div class="mb-2"><label class="form-label">Name</label><input class="form-control" name="name" required></div>
              <div class="mb-2"><label class="form-label">Symbol</label><input class="form-control" name="symbol"></div>
            </div>
            <div class="modal-footer py-2"><button class="btn btn-primary btn-sm" type="submit">Create</button></div>
          </form>
        </div>
      </div>
    </div>`;
}

function renderCurrenciesTable(root: HTMLElement, session: Session, currencies: CurrencyDto[], onOpen: (c: CurrencyDto) => void): void {
  const container = root.querySelector<HTMLElement>('#currenciesTable')!;
  const rows: RecordTableRow[] = currencies.map((c) => ({
    id: c.id,
    attrs: ' draggable="true"',
    html: `<td class="cf-title"><button type="button" data-open-currency="${c.id}">${escapeHtml(c.code)}</button></td><td>${escapeHtml(c.name)}</td>`,
  }));
  renderRecordTable(container, { session, entity: 'currency', headers: ['Code', 'Name'], rows });
  container.querySelectorAll<HTMLButtonElement>('[data-open-currency]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const currency = currencies.find((c) => c.id === btn.dataset['openCurrency']);
      if (currency) onOpen(currency);
    });
  });
}

function openCurrencyModal(root: HTMLElement, session: Session, currency: CurrencyDto): void {
  const modalEl = root.querySelector<HTMLElement>('#currencyModal')!;
  const titleEl = root.querySelector<HTMLElement>('#currencyModalTitle')!;
  const fieldsEl = root.querySelector<HTMLElement>('#currencyModalFields')!;
  titleEl.textContent = currency.code;
  fieldsEl.innerHTML = '<div id="fCurName" class="mb-2"></div><div id="fCurSymbol" class="mb-2"></div><div id="fCurTotals"></div>';

  let mounted: MountedField[] = [];
  void (async () => {
    await session.openRecord('currency', currency.id);
    mounted.push(mountCollabField(fieldsEl.querySelector('#fCurName')!, { session, entity: 'currency', recordId: currency.id, field: 'name', label: 'Name' }));
    mounted.push(mountCollabField(fieldsEl.querySelector('#fCurSymbol')!, { session, entity: 'currency', recordId: currency.id, field: 'symbol', label: 'Symbol' }));
    mounted.push(mountCollabToggle(fieldsEl.querySelector('#fCurTotals')!, { session, entity: 'currency', recordId: currency.id, field: 'useForTotals', label: 'Offer for folder totals' }));
    bsModal(modalEl)?.show();
  })();

  const onHidden = (): void => {
    session.closeRecord('currency', currency.id);
    for (const m of mounted) m.destroy();
    mounted = [];
    modalEl.removeEventListener('hidden.bs.modal', onHidden);
  };
  modalEl.addEventListener('hidden.bs.modal', onHidden);
}

function wireNewCurrencyForm(root: HTMLElement, token: string, onCreated: () => void | Promise<void>): void {
  const form = root.querySelector<HTMLFormElement>('#newCurrencyForm')!;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const code = formString(data, 'code').trim().toUpperCase();
      const name = formString(data, 'name').trim();
      const symbol = formString(data, 'symbol');
      if (!/^[A-Z]{3}$/.test(code) || !name) return;

      const res = await apiFetch('/apps/webledger/currencies', {
        method: 'POST', token, body: { id: crypto.randomUUID(), code, name, symbol },
      });
      if (res.ok) {
        form.reset();
        bsModal(root.querySelector('#newCurrencyModal')!)?.hide();
        void onCreated();
      }
    })();
  });
}

// -------------------------------------------------------------- purposes

function newTagModalHtml(): string {
  return `
    <div class="modal fade" id="newTagModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">New tag</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="newTagForm">
            <div class="modal-body">
              <div class="alert alert-danger py-2 small" id="newTagError" hidden></div>
              <div class="mb-2"><label class="form-label" for="newTagName">Name</label>
                <input class="form-control" id="newTagName" name="name" required maxlength="60"></div>
            </div>
            <div class="modal-footer py-2"><button class="btn btn-primary btn-sm" type="submit">Create</button></div>
          </form>
        </div>
      </div>
    </div>`;
}

function newPurposeModalHtml(): string {
  return `
    <div class="modal fade" id="newPurposeModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">New purpose</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="newPurposeForm">
            <div class="modal-body">
              <div class="mb-2"><label class="form-label">Name</label><input class="form-control" name="name" required></div>
            </div>
            <div class="modal-footer py-2"><button class="btn btn-primary btn-sm" type="submit">Create</button></div>
          </form>
        </div>
      </div>
    </div>`;
}

/**
 * The purposes list — a name, a drag handle and a way to remove it.
 *
 * It was a bare `<ul>` of names until `purpose_delete` and `purpose_reorder`
 * were ported; the list had no way to act on anything in it, which made the
 * `sortOrder` it is sorted by unchangeable from the UI.
 *
 * Rows rather than list items because a row can carry a handle and a button
 * without the name jumping about, and because the drag-to-reorder gesture is
 * the same one the chart of accounts uses — one gesture, learned once.
 */
/**
 * The tag manager.
 *
 * A name, how many transactions carry it, and a way to remove it — plus the
 * same drag-to-reorder gesture the purposes list uses, because the order here
 * is the order of the filter's chips and of the editor's picker.
 *
 * The usage count is the number that makes this a manager rather than a list:
 * it is what turns "delete this tag" from a guess into a decision, and it
 * shows at a glance which labels were used once and forgotten.
 */
function renderTagsTable(root: HTMLElement, tags: TagDto[], usage: Record<string, number>): void {
  const container = root.querySelector<HTMLElement>('#tagsTable')!;
  if (tags.length === 0) {
    container.innerHTML = '<p class="text-secondary small mb-0">No tags yet.</p>';
    return;
  }
  container.innerHTML = `<table class="table table-sm align-middle mb-0"><tbody>${
    tags.map((t) => {
      const n = usage[t.id] ?? 0;
      return `<tr data-tag-row="${escapeHtml(t.id)}" draggable="true">
      <td class="wl-grip" aria-hidden="true">⠿</td>
      <td>${tagChipHtml(t)}</td>
      <td class="text-secondary small">${n === 0 ? 'unused' : `on ${n} transaction${n === 1 ? '' : 's'}`}</td>
      <td class="text-end">
        <button type="button" class="btn btn-sm btn-outline-danger py-0" data-tag-delete="${escapeHtml(t.id)}"
          aria-label="Delete tag ${escapeHtml(t.name)}" title="Delete">&times;</button>
      </td>
    </tr>`;
    }).join('')
  }</tbody></table>`;
}

/**
 * Delete and reorder, delegated from the container and wired **once** — the
 * same shape as the purposes list, and for the same reason: wiring a
 * delegated listener inside a render adds one per render, and one click then
 * asks three times whether to delete.
 */
function wireTagActions(
  root: HTMLElement, token: string, tags: () => TagDto[], usage: () => Record<string, number>,
  onChanged: () => Promise<void>, fail: (m: string) => void,
): void {
  const container = root.querySelector<HTMLElement>('#tagsTable')!;
  container.addEventListener('click', (event) => {
    clearPageNotice(root);
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-tag-delete]');
    if (!button) return;
    const id = button.dataset['tagDelete']!;
    const tag = tags().find((t) => t.id === id);
    if (!tag) return;
    const n = usage()[id] ?? 0;
    void (async () => {
      // The count is in the question, because "delete tag" reads as
      // housekeeping right up until it takes a label off four hundred rows.
      const go = await askConfirm({
        title: `Delete the tag "${tag.name}"?`,
        message: [
          n === 0
            ? 'Nothing is tagged with it.'
            : `It is removed from ${n} transaction${n === 1 ? '' : 's'}. No transaction is deleted.`,
          'The deletion is soft: it stays under "Deleted records", where anyone who can '
            + 'delete it can put it back.',
        ],
        confirm: 'Delete the tag', cancel: 'Keep it', tone: 'danger',
      });
      if (!go) return;
      const res = await apiFetch(`/apps/webledger/tags/${id}`, { method: 'DELETE', token })
        .catch(() => undefined);
      if (!res?.ok) { fail(await failureMessage(res, 'Could not delete that tag.')); return; }
      await onChanged();
    })();
  });
}

/** The row drag handlers, re-wired after each render because the rows are new. */
function wireTagDrag(
  root: HTMLElement, token: string, onChanged: () => Promise<void>, fail: (m: string) => void,
): void {
  const container = root.querySelector<HTMLElement>('#tagsTable')!;
  let dragging: string | undefined;
  for (const tr of container.querySelectorAll<HTMLTableRowElement>('tr[data-tag-row]')) {
    tr.addEventListener('dragstart', (event) => {
      dragging = tr.dataset['tagRow'];
      tr.classList.add('wl-dragging');
      try { event.dataTransfer?.setData('text/plain', dragging ?? ''); } catch { /* refused */ }
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    });
    tr.addEventListener('dragover', (event) => {
      if (!dragging || dragging === tr.dataset['tagRow']) return;
      event.preventDefault();
      tr.classList.add('wl-drop-target');
    });
    tr.addEventListener('dragleave', () => tr.classList.remove('wl-drop-target'));
    tr.addEventListener('drop', (event) => {
      tr.classList.remove('wl-drop-target');
      const moved = dragging;
      dragging = undefined;
      if (!moved || moved === tr.dataset['tagRow']) return;
      event.preventDefault();
      void (async () => {
        const ids = [...container.querySelectorAll<HTMLElement>('tr[data-tag-row]')]
          .map((row) => row.dataset['tagRow']!);
        const from = ids.indexOf(moved);
        const to = ids.indexOf(tr.dataset['tagRow']!);
        if (from < 0 || to < 0 || from === to) return;
        ids.splice(to, 0, ...ids.splice(from, 1));
        const res = await apiFetch('/apps/webledger/tags/reorder', {
          method: 'POST', token, body: { orderedIds: ids },
        }).catch(() => undefined);
        if (!res?.ok) { fail(await failureMessage(res, 'Could not save the new order.')); return; }
        await onChanged();
      })();
    });
    tr.addEventListener('dragend', () => {
      dragging = undefined;
      container.querySelectorAll('.wl-drop-target').forEach((el) => el.classList.remove('wl-drop-target'));
      container.querySelectorAll('.wl-dragging').forEach((el) => el.classList.remove('wl-dragging'));
    });
  }
}

/**
 * The one refusal on this page that may not use the page notice.
 *
 * This form is inside a Bootstrap modal, which covers `#wlNotice` and greys
 * it out behind a backdrop — so a failure reported there is a failure
 * reported behind the thing the user is looking at. It goes in the modal's
 * own box, and `notify` still announces it.
 */
function wireNewTagForm(root: HTMLElement, token: string, notify: (m: string) => void, onCreated: () => void): void {
  const form = root.querySelector<HTMLFormElement>('#newTagForm')!;
  const box = root.querySelector<HTMLElement>('#newTagError');
  const failInModal = (message: string): void => { notify(message); showNoticeAt(box, message); };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      clearNoticeAt(box);
      const name = formString(new FormData(form), 'name').trim();
      if (!name) return;
      const res = await apiFetch('/apps/webledger/tags', {
        method: 'POST', token, body: { id: crypto.randomUUID(), name },
      }).catch(() => undefined);
      if (!res?.ok) { failInModal(await failureMessage(res, 'Could not create that tag.')); return; }
      form.reset();
      bsModal(root.querySelector('#newTagModal')!)?.hide();
      onCreated();
    })();
  });
}

function renderPurposesTable(root: HTMLElement, purposes: PurposeDto[]): void {
  const container = root.querySelector<HTMLElement>('#purposesTable')!;
  if (purposes.length === 0) {
    container.innerHTML = '<p class="text-secondary small mb-0">No purposes yet.</p>';
    return;
  }
  container.innerHTML = `<table class="table table-sm align-middle mb-0"><tbody>${
    purposes.map((p) => `<tr data-purpose="${escapeHtml(p.id)}" draggable="true">
      <td class="wl-grip" aria-hidden="true">⠿</td>
      <td>${escapeHtml(p.name)}</td>
      <td class="text-end">
        <button type="button" class="btn btn-sm btn-outline-danger py-0"
          data-purpose-delete="${escapeHtml(p.id)}"
          aria-label="Delete ${escapeHtml(p.name)}" title="Delete">&times;</button>
      </td>
    </tr>`).join('')
  }</tbody></table>`;
}

/**
 * The delete button, delegated from the container and wired **once**.
 *
 * Once, because the container outlives every render while the rows do not.
 * Wiring this alongside the drag handlers meant a listener per render, and
 * one click then asked three times whether to delete — the kind of bug that
 * only shows up after the list has been re-rendered a couple of times, which
 * is to say after somebody has actually used the page.
 *
 * `purposes` arrives as a getter for the same reason: this closure is made
 * once and the array it needs is replaced on every refresh.
 *
 * The confirmation names the consequence rather than asking "are you sure":
 * a purpose is a tag applied to accounts, and deleting one removes it from
 * every account that carries it. That is the part worth knowing; that the
 * delete is soft and recoverable is the part that makes it safe to say yes.
 */
function wirePurposeDeletes(
  root: HTMLElement, token: string, purposes: () => PurposeDto[],
  onChanged: () => Promise<void>, fail: (m: string) => void,
): void {
  const container = root.querySelector<HTMLElement>('#purposesTable')!;

  container.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-purpose-delete]');
    if (!button) return;
    const id = button.dataset['purposeDelete']!;
    const purpose = purposes().find((p) => p.id === id);
    if (!purpose) return;
    void (async () => {
      const go = await askConfirm({
        title: `Delete the purpose "${purpose.name}"?`,
        message: [
          'It is removed from every account tagged with it.',
          'The deletion is soft: it stays under "Deleted records", where anyone who can '
            + 'delete it can put it back.',
        ],
        confirm: 'Delete the purpose', cancel: 'Keep it', tone: 'danger',
      });
      if (!go) return;
      const res = await apiFetch(`/apps/webledger/purposes/${id}`, { method: 'DELETE', token })
        .catch(() => undefined);
      if (!res?.ok) { fail(await failureMessage(res, 'Could not delete that purpose.')); return; }
      await onChanged();
    })();
  });
}

/**
 * Drag a row onto another to move it there, and save the whole list's order.
 *
 * One for every flat list on this page that is ordered by hand — purposes,
 * and currencies. The handlers live on the rows and so are re-wired after
 * each render.
 *
 * `idAttr` names the row attribute holding the record id, in `dataset` form
 * (`purpose` for `data-purpose`, `row` for the record table's `data-row`).
 */
function wireFlatReorder(
  container: HTMLElement, idAttr: string, endpoint: string,
  token: string, onChanged: () => Promise<void>, fail: (m: string) => void,
): void {
  const selector = `tr[data-${idAttr}][draggable="true"]`;
  let dragging: string | undefined;
  for (const tr of container.querySelectorAll<HTMLTableRowElement>(selector)) {
    tr.addEventListener('dragstart', (event) => {
      dragging = tr.dataset[idAttr];
      tr.classList.add('wl-dragging');
      // Some browsers refuse a drag with no payload, so set one even though
      // the id is tracked in this closure.
      try { event.dataTransfer?.setData('text/plain', dragging ?? ''); } catch { /* refused */ }
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    });
    tr.addEventListener('dragover', (event) => {
      if (!dragging || dragging === tr.dataset[idAttr]) return;
      event.preventDefault();
      tr.classList.add('wl-drop-target');
    });
    tr.addEventListener('dragleave', () => tr.classList.remove('wl-drop-target'));
    tr.addEventListener('drop', (event) => {
      tr.classList.remove('wl-drop-target');
      const moved = dragging;
      dragging = undefined;
      if (!moved || moved === tr.dataset[idAttr]) return;
      event.preventDefault();
      void applyFlatReorder(container, selector, idAttr, moved, tr.dataset[idAttr]!, endpoint, token, onChanged, fail);
    });
    tr.addEventListener('dragend', () => {
      dragging = undefined;
      container.querySelectorAll('.wl-drop-target').forEach((el) => el.classList.remove('wl-drop-target'));
      container.querySelectorAll('.wl-dragging').forEach((el) => el.classList.remove('wl-dragging'));
    });
  }
}

/**
 * Send the whole list in its new order.
 *
 * The order is read back out of the DOM rather than recomputed from the data,
 * so what the server is told to save is exactly what the person was looking
 * at — and the server refuses anything that is not a permutation of the full
 * set, which is what stops a partial list renumbering three rows on top of
 * the others.
 */
async function applyFlatReorder(
  container: HTMLElement, selector: string, idAttr: string, movedId: string, targetId: string,
  endpoint: string, token: string, onChanged: () => Promise<void>, fail: (m: string) => void,
): Promise<void> {
  const ids = [...container.querySelectorAll<HTMLElement>(selector)].map((row) => row.dataset[idAttr]!);
  const from = ids.indexOf(movedId);
  const to = ids.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return;
  ids.splice(to, 0, ...ids.splice(from, 1));

  const res = await apiFetch(endpoint, { method: 'POST', token, body: { orderedIds: ids } })
    .catch(() => undefined);
  if (!res?.ok) { fail(await failureMessage(res, 'Could not save the new order.')); return; }
  await onChanged();
}

function wireNewPurposeForm(root: HTMLElement, token: string, onCreated: () => void | Promise<void>): void {
  const form = root.querySelector<HTMLFormElement>('#newPurposeForm')!;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const name = formString(new FormData(form), 'name').trim();
      if (!name) return;
      const res = await apiFetch('/apps/webledger/purposes', { method: 'POST', token, body: { id: crypto.randomUUID(), name } });
      if (res.ok) {
        form.reset();
        bsModal(root.querySelector('#newPurposeModal')!)?.hide();
        void onCreated();
      }
    })();
  });
}


// ---------------------------------------------------------------- exchange rates

interface FxResponse {
  base: string; date: string | null; rates: Record<string, number>;
  stale: boolean; error: string | null;
  totalsCurrencies: Array<{ code: string; name: string }>;
}

/**
 * Legacy's "Refresh now" + status line.
 *
 * Reading the rates is what *causes* the daily fetch — `getRates` fetches
 * when the cached blob is not from today — so simply opening this page is
 * usually the thing that refreshes them. The button is for the case the
 * automatic path cannot cover: rates that changed since this morning's fetch,
 * or an upstream that was down when the day's first request went out.
 */
async function mountExchangeRates(root: HTMLElement, token: string): Promise<void> {
  const statusEl = root.querySelector<HTMLElement>('#wlFxStatus');
  const button = root.querySelector<HTMLButtonElement>('#wlFxRefresh');
  if (!statusEl || !button) return;

  const render = (fx: FxResponse | undefined): void => {
    if (!fx) {
      statusEl.textContent = 'Exchange rates are unavailable offline.';
      return;
    }
    const offered = fx.totalsCurrencies.map((c) => c.code).join(', ') || 'none';
    const when = fx.date ? `Rates from ${fx.date}` : 'No rates have been fetched yet';
    // The staleness message comes from the server rather than being inferred
    // here: it is the server that knows whether the fetch failed or the cache
    // simply had not aged out, and those read very differently.
    statusEl.innerHTML = fx.error
      ? `<span class="wl-fx-warn">⚠ ${escapeHtml(fx.error)}</span> `
        + `<span class="text-secondary">Offered for totals: ${escapeHtml(offered)}.</span>`
      : `${escapeHtml(when)}, base ${escapeHtml(fx.base)}, ${Object.keys(fx.rates).length} currencies. `
        + `Offered for totals: ${escapeHtml(offered)}.`;
  };

  const first = await apiFetch('/apps/webledger/fx', { token }).catch(() => undefined);
  render(first?.ok ? (await first.json()) as FxResponse : undefined);

  button.addEventListener('click', () => {
    void (async () => {
      button.disabled = true;
      statusEl.textContent = 'Refreshing…';
      const res = await apiFetch('/apps/webledger/fx/refresh', { method: 'POST', token }).catch(() => undefined);
      if (res?.ok) render((await res.json()) as FxResponse);
      else statusEl.innerHTML = '<span class="wl-fx-warn">⚠ Could not reach the exchange-rate service, '
        + 'and nothing is cached to fall back on.</span>';
      button.disabled = false;
    })();
  });
}
